/**
 * Shared utilities for realtime providers (Pusher & Centrifugo)
 */

import { jwtDecrypt } from '@/shared/helper'

// ---------------------------------------------------------------------------
// Event Mappings (provider event name → DOM CustomEvent name)
// ---------------------------------------------------------------------------

// Live Chat Channel Events ({shopname}-livechat)
export const LIVECHAT_CHANNEL_EVENTS: Record<string, string> = {
  'my-event':              'new-message',
  'remove-conversation':   'remove-conversation',
  'customer_info_update':  'customer_info_update',
  'livechat_online_status': 'livechat_online_status',
}

// Broadcast Channel Events ({shopname}-broadcast)
export const BROADCAST_CHANNEL_EVENTS: Record<string, string> = {
  'broadcast-update':      'broadcast-update',
}

// WhatsApp Channel Events ({shopname}-whatsapp)
export const WHATSAPP_CHANNEL_EVENTS: Record<string, string> = {
  'template-update':       'template-update',
}

// Notification Channel Events (authkey) - unchanged
export const NOTIFICATION_CHANNEL_EVENTS: Record<string, string> = {
  'get-conversation':      'new-message-via-agent-routing',
  'reassign-conversation': 'reassign-conversation',
}

// Deprecated: Use individual channel event maps above
export const SHOP_CHANNEL_EVENTS: Record<string, string> = {
  ...LIVECHAT_CHANNEL_EVENTS,
  ...BROADCAST_CHANNEL_EVENTS,
  ...WHATSAPP_CHANNEL_EVENTS,
}

// ---------------------------------------------------------------------------
// Shared Helpers
// ---------------------------------------------------------------------------

/**
 * Dispatch a CustomEvent on the document for downstream consumers.
 * Both Pusher and Centrifugo use this to emit the same DOM events.
 */
export function dispatchDomEvent(domEvent: string, data: unknown): void {
  document.dispatchEvent(new CustomEvent(domEvent, { detail: data }))
}

/**
 * Extract the force-logout channel name (role_id, base64-encoded) from a JWT token.
 * Returns null if the token doesn't contain a role_id.
 */
export function resolveForceLogoutChannelName(token: string): string | null {
  const tokenPayload = jwtDecrypt(token)
  const channelIdentifier = tokenPayload?.role_id || tokenPayload?.userid || tokenPayload?.user_id
  if (!channelIdentifier) return null
  return btoa(String(channelIdentifier))
}

/**
 * Determine if a force_logout event should be handled for this session.
 * Returns false if the event targets other sessions (exclude_jti match).
 */
export function shouldHandleForceLogout(data: any, refreshToken?: string): boolean {
  if (data?.exclude_jti && refreshToken) {
    const refreshPayload = jwtDecrypt(refreshToken)
    if (refreshPayload?.jti === data.exclude_jti) {
      console.info('force_logout skipped — current session excluded (matching jti)')
      return false
    }
  }
  return true
}
